import * as React from 'react';
import moment from 'moment';
import GenericHoc from 'src/components/hoc/GenericHoc';
import { HocOptions } from 'src/components/hoc/GenericHoc.types';
import { siStyles } from './document-request.styles';
import { StylesProps } from 'src/Library/Types';
import Collapse from 'antd/lib/collapse';
import Icon from 'antd/lib/icon';
import Button from 'antd/lib/button';
import Spin from 'antd/lib/spin';
import Modal from 'antd/lib/modal';
import message from 'antd/lib/message';
import { statusColorMap } from './document-request.constants';
import { bindActionCreators } from 'redux';
import { fetchAllSEDocuments, fetchShipmentInstructionDetails } from 'src/actions/shipmentExecutionActions';
import GetExecutionData from 'src/dto/response/getExecution';
import { getSIPartyAddress } from '../../SE.utils';
import { isValidArray, isValidString } from 'src/Library/Helper';
import { nl2Br } from 'src/utils/DOMUtils';
import isEmpty from 'lodash/isEmpty';
import { useGenericState } from 'src/Library/UseGenericState';
import { deleteSIRequest } from 'src/api/shipmentExecution';
import { siInttraStatus } from '../SETasks/SETaskList/UploadSIModal.utils';
import Loader from 'src/components/common/Loader';

interface SIProps extends StylesProps<typeof siStyles> {
  shippingInstructions: {
    id: string;
    isDraft: boolean
    referenceNumber: string;
    createdAt: moment.Moment;
    inttraStatus: string | null;
  }[];
  siRequestData;
  shipmentDetails;
  pendingSIContainers;
  setUploadSIModalStates;
  actionsEnabled: boolean;
  isFetchingShippingData: boolean;
  selectedShipment: GetExecutionData;
  fetchAllSEDocuments: (shipmentId: string) => void;
  renderStatusDot: (status: string | null) => React.ReactElement;
  fetchShipmentInstructionDetails: (shipmentId: string | number, shippingInstructionId: string) => void;
}

interface StateProps {
  isSIRequestDeleting?: boolean;
  isDeleteSIRequestModalVisible?: boolean;
}

const SIDocs = (props: SIProps) => {
  const {
    classes,
    actionsEnabled,
    shipmentDetails,
    renderStatusDot,
    selectedShipment,
    pendingSIContainers,
    fetchAllSEDocuments,
    shippingInstructions,
    setUploadSIModalStates,
    siRequestData: details,
    isFetchingShippingData,
    fetchShipmentInstructionDetails,
  } = props;

  const [active, setActive] = React.useState<number>(0);
  const [state, setState] = useGenericState<StateProps>({});

  const {
    isSIRequestDeleting,
    isDeleteSIRequestModalVisible,
  } = state;

  React.useEffect(() => {
    const shippingInstructionId = shippingInstructions?.[active]?.id;
    if (!selectedShipment?.id || (!active && active !== 0) || !shippingInstructionId) {
      return;
    }

    fetchShipmentInstructionDetails(selectedShipment?.id, shippingInstructionId);
  }, [active, selectedShipment]);

  const renderSIList = () => {
    const isNewRequestDisabled = !actionsEnabled || pendingSIContainers <= 0;

    return <div className={classes.docList}>
      {shippingInstructions?.map((item, index) => <div
        key={index}
        onClick={() => setActive(index)}
        className={classes.docCard}
        style={{
          color: active === index ? '#FFFFFF' : 'inherit',
          backgroundColor: active === index ? '#444444' : 'inherit',
          fontWeight: active === index ? 'bold' : 'inherit',
        }}
      >
        <div className={classes.docName}>
          {`SI Request ${index + 1}`}
          {renderStatusDot(item.inttraStatus)}
        </div>
        <div className={classes.docDate}>
          {item.createdAt && moment(item.createdAt).format('DD MMM YYYY | hh:mm A')}
        </div>
      </div>)}
      <div
        className={classes.addDocument}
        onClick={isNewRequestDisabled ?
          undefined :
          () => setUploadSIModalStates({ isUploadSIModalVisible: true })
        }
        style={isNewRequestDisabled ?
          { cursor: 'not-allowed', color: 'lightgrey' } :
          undefined
        }
      >
        + New Request
      </div>
    </div>;
  };

  const renderDeleteModal = () : React.ReactElement => {
    return (
      <Modal
        title="Confirmation"
        confirmLoading={isSIRequestDeleting}
        visible={isDeleteSIRequestModalVisible}
        onOk={handleDelete}
        width={350}
        okText="Yes"
        cancelText="No"
        onCancel={() => setState({ isDeleteSIRequestModalVisible: false })}
      >
        Are you sure you want to delete this draft?
      </Modal>
    );
  };

  const handleDelete = async () : Promise<void> => {
    setState({ isSIRequestDeleting: true });
    const response = await deleteSIRequest(details?.draftId);
    if (response.isSuccess) {
      message.success('SI Request Deleted Successfully');
      fetchAllSEDocuments(shipmentDetails?.id);
      setState({ isSIRequestDeleting: false, isDeleteSIRequestModalVisible: false });
    } else {
      message.error(response.errorMessage);
      setState({ isSIRequestDeleting: false });
    }
  }

  const renderEditAndDelete = () => {
    if (!details?.isDraft) {
      return;
    }

    return (
      <>
        <Icon type="edit"
          style={{ marginLeft: 8, cursor: pendingSIContainers <= 0 ? 'not-allowed' : 'pointer' }}
          theme="filled"
          onClick={() => {
            if (pendingSIContainers <= 0) return;
            setUploadSIModalStates({ isUploadSIModalVisible: true })
          }}
        />
        {isSIRequestDeleting ? <Spin /> :
          <Icon type="delete"
            style={{ marginLeft: 8 }}
            theme="filled"
            onClick={() => setState({ isDeleteSIRequestModalVisible: true })}
          />}
      </>
    );
  }

  const renderAmendRequest = () => {
    if (
      (details?.inttraStatus === siInttraStatus.REQUESTED) &&
      (
        !(shipmentDetails?.siCutOff) ||
        (shipmentDetails?.siCutOff && moment().diff(shipmentDetails?.siCutOff, 'seconds') <= 0)
      )
    ) {
      return (
        <div style={{
          fontWeight: 600,
          color: '#0780FE',
          cursor: 'pointer',
          fontSize: 14,
          marginLeft: 8,
        }}
          onClick={() => {
            setUploadSIModalStates({
              isUploadSIModalVisible: true,
              isAmend: true,
            })
          }}
        >
          Amend Request
        </div>
      );
    }
    return;
  }

  const renderRetry = () => {
    if (details?.inttraStatus === siInttraStatus.REJECTED) {
      return (
        <div style={{
          fontWeight: 600,
          color: '#F5222D',
          cursor: 'pointer',
          fontSize: 14,
          marginLeft: 8,
        }}
          onClick={() => {
            setUploadSIModalStates({
              isUploadSIModalVisible: true,
              isRetryError: true,
            })
          }}
        >
          Retry Error
        </div>
      );
    }
    return;
  }

  const renderDocumentActions = () => {
    if (!actionsEnabled) {
      return;
    }

    return <>
      {renderAmendRequest()}
      {renderRetry()}
      {renderEditAndDelete()}
    </>;
  }

  const renderReference = () => {
    return (
      <div className={classes.reference}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div className={classes.referenceList}>
            <div className={classes.referenceValue}>{details?.referenceNumber || '-'}</div>
            <div className={classes.referenceTitle}>SI Request #</div>
          </div>
          <div className={classes.referenceList}>
            {details?.isDraft &&
            <span className={classes.flexRow} style={{ marginLeft: '4px' }}>
              <strong>|</strong> <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 16,
                  backgroundColor: '#666666',
                  margin: '0 4px',
                }}
              /> Draft
            </span>}
          </div>
        </div>
        <div style={{
          fontSize: 20,
          display: 'flex',
        }}>
          {renderDocumentActions()}
        </div>
      </div>
    );
  };

  const renderTitle = (title: string) => {
    return <div 
      className={classes.title}>
      {title}
    </div>;
  };
  const renderValue = (value: string | number | undefined, isDate: boolean = false) => {
    if (!value || value === 'N.A.') {
      return <div
        className={classes.na}>
          NA
      </div>;
    }
    if (isDate) {
      return <div
        className={classes.value}>
        {moment(value).format('DD/MM/YYYY | HH:mm A')}
      </div>;
    }
    return <div className={classes.value}>{value}</div>;
  };

  const renderField = (title: string, value: string | number | undefined, isDate: boolean = false) => {
    return <div
      className={classes.flexRow}
      style={{ width: '33%', alignItems: 'normal' }}>
      {renderTitle(title)} {renderValue(value, isDate)}
    </div>;
  };

  const renderContentTitle = (title: string) => {
    return <div 
      className={classes.contentTitle}>
      {title}
    </div>;
  };

  const renderLine = () => {
    return <div className={classes.line}/>;
  };

  const renderPartiesColumn = () => {
    return <div className={classes.flexRow}>
      <div style={{ width: '15%' }} className={classes.title}/>
      <div style={{ width: '25%' }} className={classes.title}>Name</div>
      <div style={{ width: '40%' }} className={classes.title}>Address</div>
    </div>;
  };

  const renderPartiesValue = () => {
    const stakeHolders = details?.stakeHolders;
    return stakeHolders.map(item => <div
        className={classes.flexRow}
        style={{
          margin: '16px 0px',
          alignItems: 'normal',
        }}
      >
      <div style={{ width: '15%' }} className={classes.title}>{item.type?.label || '-'}</div>
      <div style={{ width: '25%' }} className={classes.value}>{item.name || '-'}</div>
      <div style={{ width: '40%' }} className={classes.value}>{getSIPartyAddress(item.address)}</div>
    </div>);
  };

  const renderParties = () => {
    return <div>
      {renderPartiesColumn()}
      {renderPartiesValue()}
    </div>;
  };

  const renderPartiesInvolved = () => {
    const { stakeHolders } = details || {};
    if (!stakeHolders?.length) {
      return;
    }

    return <div className={classes.contentBox}>
      {renderContentTitle('Parties Involved')}
      {renderLine()}
      {renderParties()}
    </div>;
  };

  const renderBooking = () => {
    return <div className={classes.contentBox}>
      {renderContentTitle('Booking Details')}
      {renderLine()}
      <div className={classes.contentRow}>
        {renderField('Carrier', details?.carrier?.name)}
        {renderField('Vessel', details?.vessel)}
        {renderField('Voyage', details?.voyage)}
      </div>
      <div className={classes.contentRow}>
        {renderField('Booking No.', details?.bookingNumber)}
      </div>
    </div>;
  };

  const renderAddresses = () => {
    const movementType = details?.movementType;
    const isPOLDoorMovement = movementType && ['Door to Door', 'Door to Port'].includes(movementType);
    const isPODDoorMovement = movementType && ['Door to Door', 'Port to Door'].includes(movementType);
    const originAddress = isPOLDoorMovement && isValidString(details?.originAddress) ?
      nl2Br(details?.originAddress, '\n')
      : '-';
    const destinationAddress = isPODDoorMovement && isValidString(details?.destinationAddress) ?
      nl2Br(details?.destinationAddress, '\n')
      : '-';

    if (!isPOLDoorMovement && !isPODDoorMovement) {
      return;
    }

    return (
      <div className={classes.contentRow}>
        {renderField('', ' ')}
        {renderField('Origin Address', originAddress as string)}
        {renderField('Destination Address', destinationAddress as string)}
      </div>
    );
  }

  const renderLocation = () => {    
    return <div className={classes.contentBox}>
      {renderContentTitle('Location Details')}
      {renderLine()}
      <div className={classes.contentRow}>
        {renderField('Movement Type', details?.movementType)}
        {renderField('Place of Receipt', details?.placeOfReceipt?.name)}
        {renderField('Place of Delivery ', details?.placeOfDelivery?.name)}
      </div>
      <div className={classes.contentRow}>
        {renderField('', ' ')}
        {renderField('POL', details?.portOfLoading?.name)}
        {renderField('POD', details?.portOfDischarge?.name)}
      </div>
      {renderAddresses()}
      <div className={classes.contentRow} style={{ marginTop: 0, marginBottom: 0 }}>
        <div style={{ width: '33%'}} />
        <div style={{
          width: '33%',
          paddingLeft: 12,
          color: '#424242',
          fontWeight: 'bold',
        }}>Print on B/L as</div>
        <div style={{ width: '33%'}} />
      </div>
      <div className={classes.contentRow}>
        {renderField('', ' ')}
        {renderField('Place of Receipt', details?.blPrintDetails?.placeOfReceipt)}
        {renderField('Place of Delivery ', details?.blPrintDetails?.placeOfDelivery)}
      </div>
      <div className={classes.contentRow}>
        {renderField('', ' ')}
        {renderField('POL', details?.blPrintDetails?.pol)}
        {renderField('POD', details?.blPrintDetails?.pod)}
      </div>
    </div>;
  };

  const renderAction = (status) => {
    switch (status) {
      case 'Rejected':  return <Button type="link" className={classes.button}>Submit A New Request</Button>;
      case 'Pending': return <Icon type="reload" style={{ fontSize: 18 }}/>;
      default: return null;
    }
  };

  const renderStatus = (status) => {
    return <div
      className={classes.status}
      style={{
        color: statusColorMap[status].color,
        backgroundColor: statusColorMap[status].background,
        border: statusColorMap[status].border,
      }}
    >
      {status}
    </div>;
  };

  const renderCollapseHeader = ({
    title,
    status,
  }: {
    title: string;
    status: string;
  }) => {
    return <div
      className={classes.flexRow}
      style={{ justifyContent: 'space-between' }}
    >
      <div className={classes.flexRow}>
        <div className={classes.containerTitle}>{title}</div>
        {/* {renderStatus(status)} */}
      </div>
      {/* {renderAction(status)} */}
    </div>;
  };

  const renderSealDetails = (sealDetails) => {
    return isValidArray(sealDetails) && <>
      <div style={{ width: '50%'}}>
        <div className={classes.contentRow}>
          <div className={classes.title} style={{ minWidth: '20%' }}>S. No.</div>
          <div className={classes.title} style={{ minWidth: '60%' }}>Type</div>
          <div className={classes.title} style={{ minWidth: '20%' }}>Value</div>
        </div>
        {sealDetails?.map((seal, index) => (
          <div className={classes.contentRow} key={index}>
            <div className={classes.value} style={{ minWidth: '20%' }}>{index + 1}</div>
            <div className={classes.value} style={{ minWidth: '60%' }}>{seal.type?.label}</div>
            <div className={classes.value} style={{ minWidth: '20%' }}>{seal.value}</div>
          </div>
        ))}
      </div>
    </>;
  }

  const renderContent = (item) => {
    const { sealDetails } = item || {};

    return <React.Fragment>
      <div className={classes.contentBox}>
        <div className={classes.contentRow}>
          {renderField('Type & Size', `${item.containerSize} ${item.containerType}`)}
          {renderField('Container Gross Weight', renderWeightField(item.containerGrossWeight))}
          {renderField('Tare Weight', renderWeightField(item.containerTareWeight))}
        </div>
      </div>
      <div className={classes.contentBox}>
        {renderContentTitle('Seal Number')}
        {renderSealDetails(sealDetails)}
      </div>
      <div className={classes.contentBox}>
        {renderContentTitle('Cargo Details')}
        <div className={classes.contentRow}>
          {renderField('Package Count', item.packageCount)}
          {renderField('Cargo Gross Wt', renderWeightField(item.grossWeight))}
        </div>
      </div>
    </React.Fragment>;
  };

  const renderContainerList = () => {
    const { containerDetails } = details?.cargoAndContainerDetails || {};
    if (!containerDetails?.length) {
      return;
    }

    return containerDetails.map((item) => <Collapse
      className={classes.collapse}
    >
      <Collapse.Panel
        header={renderCollapseHeader({
          title: `Container ${item.containerNumber}`,
          status: 'Accepted',
        })}
        key={item.containerNumber}
      >
        {renderContent(item)}
      </Collapse.Panel>
    </Collapse>);
  };

  const renderInfo = () => {
    return <span
      className={classes.info}>
      There’s only cargo in this shipment
    </span>;
  };

  const renderIntermediate = (title: string) => {
    return <div className={classes.intermediate}>{title}</div>;
  };

  const renderWeightField = (data : { value: number, unit: string }) => {
    const { value, unit } = data || {};
    if (!value || !unit) {
      return '';
    }

    return `${value} ${unit}`;
  }

  const renderCargoDetails = () => {
    const { cargoDetails } = details?.cargoAndContainerDetails || {};
    if (!cargoDetails) {
      return;
    }

    return <>
      {renderInfo()}
      {renderIntermediate('Cargo')}
      <div className={classes.contentRow}>
        {renderField('Package Type', cargoDetails.packageType?.description)}
        {renderField('Print on B/L as', cargoDetails.packageTypeOnBl)}
        {renderField('HS Code', cargoDetails?.hsCode)}
      </div>
      <div className={classes.contentRow}>
        {renderField('Cargo Gross Vol', renderWeightField(cargoDetails.grossVolume))}
        {renderField('Marks & Numbers', cargoDetails.marksAndNumbers)}
      </div>
      <div className={classes.contentRow}>
        <span
          className={classes.title}
          style={{ width: '15%' }}>
          Cargo Description
        </span>
        <span
          className={classes.value}
          style={{ width: '70%' }}
        >
          {cargoDetails?.description}
        </span>
      </div>
    </>;
  };

  const renderCargo = () => {
    return <div className={classes.contentBox}>
      {renderContentTitle('Container & Cargo')}
      <div className={classes.line} style={{ marginBottom: 8 }}/>
      {renderCargoDetails()}
      {renderContainerList()}
    </div>;
  };

  const renderPaymentColumns = () => {
    return <div className={classes.flexRow} style={{ marginBottom: 16 }}>
      <div style={{ width: '5%'}} className={classes.title}>S No.</div>
      <div style={{ width: '25%'}} className={classes.title}>Charge Name</div>
      <div style={{ width: '10%'}} className={classes.title}>Freight term</div>
      <div style={{ width: '10%'}} className={classes.title}>Payer</div>
      <div style={{ width: '25%'}} className={classes.title}>Name</div>
    </div>;
  };

  const renderPaymentValues = () => {
    const { paymentDetails } = details || {};
    return paymentDetails?.map((item, idx) => <div className={classes.flexRow} style={{ marginBottom: 16 }}>
      <div style={{ width: '5%' }} className={classes.value}>{idx + 1}</div>
      <div style={{ width: '25%' }} className={classes.value}>{item.chargeType?.label || '-'}</div>
      <div style={{ width: '10%' }} className={classes.value}>{item.freightTerms?.label || '-'}</div>
      <div style={{ width: '10%' }} className={classes.value}>{item.payer?.label || '-'}</div>
      <div style={{ width: '25%' }} className={classes.value}>{item.name || '-'}</div>
    </div>);
  };

  const renderPayment = () => {
    return <div className={classes.contentBox}>
      {renderContentTitle('Payment Details')}
      {renderLine()}
      {renderPaymentColumns()}
      {renderPaymentValues()}
    </div>;
  };

  const renderNonNegotiable = () => {
    return <React.Fragment>
      {renderIntermediate('Non Negotiable')}
      <div className={classes.contentRow}>
        {renderField('Freighted', details?.blInstructions?.freighted?.nonNegotiable)}
        {renderField('Unfreighted', details?.blInstructions?.unfreighted?.nonNegotiable)}
      </div>
    </React.Fragment>;
  };

  const renderBLInstructions = () => {
    return <div className={classes.contentBox}>
      {renderContentTitle('BL Instructions')}
      {renderLine()}
      <div className={classes.contentRow}>
        {renderField('Document Type', details?.blInstructions?.documentType?.label)}
        {renderField('BL Release Date', details?.blInstructions?.blReleaseDate?.dateString, true)}
      </div>
      <div className={classes.contentRow}>
        {renderField('Freighted', details?.blInstructions?.freighted?.default)}
        {renderField('Unfreighted', details?.blInstructions?.unfreighted?.default)}
      </div>
      <div className={classes.contentRow}>
        {renderField('BL Release Office', !isEmpty(details?.blInstructions?.blReleaseOffice) ?
          `${details?.blInstructions?.blReleaseOffice?.name} (${details?.blInstructions?.blReleaseOffice?.code})`
          : '-')}
      </div>
      {renderNonNegotiable()}
    </div>;
  };

  const renderCustomCompliance = () => {
    const shipper = details?.customsCompliance?.parties?.find(item => item.type === 'Shipper');
    const consignee = details?.customsCompliance?.parties?.find(item => item.type === 'Consignee');

    return <div className={classes.contentBox}>
      {renderContentTitle('Custom Compliance')}
      {renderLine()}
      <div className={classes.contentRow}>
        {renderField('Shipper Tax ID', details?.customsCompliance?.shipperTaxId)}
        {renderField('Carrier Tax ID', details?.customsCompliance?.carrierTaxId)}
        {renderField('Filling Status', details?.customsCompliance?.filingStatus?.label)}
      </div>
      {details?.customsCompliance?.filingStatus?.key &&
        <>
          <div className={classes.contentRow}>
            {renderField('Shipper Name', shipper?.name)}
            {renderField('Consignee Name', consignee?.name)}
          </div>
          <div className={classes.contentRow}>
            {renderField(
              'Shipper Address',
              getSIPartyAddress(shipper?.address),
              )}
            {renderField(
              'Consignee Address',
              getSIPartyAddress(consignee?.address),
              )}
          </div>
        </>
      }
    </div>;
  };

  const renderReferenceNumbers = () => {
    return <div className={classes.contentBox}>
      {renderContentTitle('Reference Numbers')}
      {renderLine()}
      <div className={classes.contentRow}>
        {details?.referenceNumbers?.map((item, index) => {
          return <>
            {renderField('Export Ref No.', item.type?.label)}
            {renderField('Other Ref No.', item.value)}
          </>
        })}
      </div>
    </div>;
  };

  const renderNotifyColumns = () => {
    return <div className={classes.flexRow} style={{ marginBottom: 16 }}>
      <div className={classes.title} style={{ width: '5%' }}>S No.</div>
      <div className={classes.title} style={{ width: '15%' }}>Name</div>
      <div className={classes.title} style={{ width: '15%' }}>Email</div>
    </div>;
  };

  const renderNotifyValues = (arr) => {
    return arr?.map((item, idx) => <div
      className={classes.flexRow}
      style={{ marginBottom: 16 }}>
      <div className={classes.value} style={{ width: '5%' }}>{idx + 1}</div>
      <div className={classes.value} style={{ width: '15%' }}>{item.name}</div>
      <div className={classes.value} style={{ width: '15%' }}>{item.email}</div>
    </div>);
  };

  const renderNotifyTable = (arr) => {
    return <div style={{ marginLeft: '4%' }}>
      {renderNotifyColumns()}
      {renderNotifyValues(arr)}
    </div>;
  };

  const renderInttraMessage = () => {
    return (
      <div>* <span className={classes.inttraNotificationMessage}>
        INTTRA notifications will be sent to the above users.
        </span>
      </div>
    );
  }
  const renderNotify = () => {
    const { notifyUsers, partnerNotifyUsers } = details || {};
    return <div className={classes.contentBox}>
      {renderContentTitle('Notify Users')}
      {renderLine()}
      {renderIntermediate('Carrier Notifications')}
      {renderNotifyTable(notifyUsers)}
      {isValidArray(details?.partnerNotifyUsers) ? (
        <>
          {renderIntermediate('INTTRA Notifications')}
          {renderNotifyTable(partnerNotifyUsers)}
        </>
      ) : (
          renderInttraMessage()
        )
      }
    </div>;
  };

  if (isFetchingShippingData) {
    return <Loader
      style={{
        marginLeft: '18%',
        marginTop: '6%',
      }}
      zIndex={1}
    />;
  }

  return <div className={classes.main}>
    {renderSIList()}
    {!!shippingInstructions?.length && (
      <>
        {renderReference()}
        {isValidArray(details?.stakeHolders) && renderPartiesInvolved()}
        {renderBooking()}
        {renderLocation()}
        {(!isEmpty(details?.cargoAndContainerDetails?.cargoDetails) ||
          isValidArray(details?.cargoAndContainerDetails?.containerDetails)) &&
          renderCargo()}
        {isValidArray(details?.paymentDetails) && renderPayment()}
        {renderBLInstructions()}
        {renderCustomCompliance()}
        {isValidArray(details?.referenceNumbers) && renderReferenceNumbers()}
        {(isValidArray(details?.notifyUsers) ||
          isValidArray(details?.partnerNotifyUsers)) && renderNotify()}
        {isDeleteSIRequestModalVisible && renderDeleteModal()}
      </>
    )}
  </div>;
};

const mapStateToProps = ({ shipmentExecutionReducer, masterData }) => ({
  siRequestData: shipmentExecutionReducer.siRequestData,
  shippingInstructions: shipmentExecutionReducer.shippingInstructions,
  pendingSIContainers: shipmentExecutionReducer.siPendingCount?.SI?.pendingContainerCount,
  isFetchingShippingData: shipmentExecutionReducer.isFetchingShippingData,
});

const mapDispatchToProps = dispatch => bindActionCreators({
  fetchAllSEDocuments,
  fetchShipmentInstructionDetails,
}, dispatch);

const options: HocOptions = {
  connectRedux: {
    useRedux: true,
    mapStateToProps,
    mapDispatchToProps,
  },
  connectJss: {
    useJss: true,
    styleSheet: siStyles,
  },
};

const SI = GenericHoc(options)(SIDocs);
export default SI;
